<template>
  <!--
  Local URL: http://localhost:8080/vectraits-datasets
  Required Group Membership: None
  Purpose: This page is for all users to browse, search and download VecDyn data quickly. The bulk of this page is a 
  search form and a table displaying results.
  The user can select one or more checkboxes and click "Download" to download the records belonging to those datasets 
  to their local machine.
  Clicking on a row will take the user to that dataset's detail displaying more information about the dataset 
  including a map and some basic data visualization.
  WHAT'S NOT COMPLETE:
    Much of the functionality of this page was replicated in the ExplorerSearchFormAndTable component. This page was 
    build before that component. When I began work on the VecDyn version of this page, I decided to build that 
    functionality into a component that could be used in both places. I didn't get a chance to return to this page 
    and convert it to use the new component.
    There are some issues with the ExplorerSearchFormAndTable component that will need to be resolved before this 
    page can use it. See the notes on that component: 
        @/components/forms/ExplorerSearchFormAndTable.vue
-->
  <VTBaseTemplate>
    <b-modal
      id="api-err-modal"
      v-model="apiErrModalShow"
      centered
      scrollable
      hide-footer
      hide-header-close
      no-close-on-backdrop
      no-close-on-esc
      title="Error"
      :header-bg-variant="'warning'"
      :header-text-variant="'light'"
    >
      <span v-html="err_msg"></span>
      <b-button class="mt-3" block @click="hideModal">OK</b-button>
    </b-modal>
    <b-modal
        id="download-csv-modal"
        v-model="downloadCSVModal"
        centered
        scrollable
        hide-footer
        hide-header-close
        no-close-on-backdrop
        no-close-on-esc
        title="Data Use Policy"
        :header-bg-variant="'dark'"
        :header-text-variant="'light'">
        <span v-html="'It is important for data publishers and for the reputation of data users that data is cited correctly.  Citation information for each record can be found inside the downloaded file. We would be grateful for you to also <a href=https://www.vectorbyte.org/blog/how-to-cite-us target=_blank rel=noopener noreferrer style=text-decoration:underline;>cite the VectorByte resource</a>. Data should be presumed to be licensed under <a href=https://creativecommons.org/licenses/by-nc/4.0/ target=_blank rel=noopener noreferrer style=text-decoration:underline;>CC-BY-NC</a>, but data generators may have used a more permissive license. Please check with the original data generator when in doubt.'"></span>
        <br>
        <b-button class="mt-3" style="margin-right:20px; margin-left:100px;" @click="hideModal">Cancel</b-button>
        <b-button class="mt-3" variant='primary' @click="downloadCSV">Agree and Download</b-button>
    </b-modal> 
    <div class="container-fluid flex-wrapper">
      <div class="flex-row height-by-content">
        <div class="title-area">
          <center>
            <br /><br /><br />
            <img
              src="@/assets/images/logos/vectrait_logo.svg"
              class="mr-2"
              :width="logoWidth"
            />
            <h2>VecTraits Explorer</h2>
          </center>
        </div>
        <ExplorerInstructions />
        <b-row>
          <b-col cols="9" class="mr-1 mt-1">
            <b-form-input
              v-model="keywords"
              placeholder="Search by phrase..."
              @keypress.enter="searchByKeyword()"
            ></b-form-input>
          </b-col>
          <b-col cols="3" class="mr-1 mt-1"></b-col>
        </b-row>
        <b-row v-for="index in queryFieldsetCount" :key="index">
          <b-input-group>
            <b-col cols="3" class="mt-1">
              <b-form-select
                v-model="fieldArray[index - 1]"
                :options="fieldDropdownOptions"
              >
              </b-form-select>
            </b-col>
            <b-col cols="3" class="mt-1">
              <b-form-select v-model="operatorArray[index - 1]">
                <option
                  v-for="(operator, idx) in operatorOptionsArray[index - 1]"
                  :key="idx"
                  :value="operator.value"
                  :title="operator.title || null"
                >
                  {{ operator.text }}
                </option>
              </b-form-select>
            </b-col>
            <b-col cols="3" class="mt-1">
              <b-form-input
                v-model="termArray[index - 1]"
                placeholder="Enter a search term..."
                @keypress.enter="searchByKeyword()"
              ></b-form-input>
            </b-col>
            <b-col class="mt-1">
              <b-button
                v-if="queryFieldsetCount > 1"
                class="mr-1"
                size="sm"
                @click="removeQueryFieldset(index - 1)"
                >-</b-button
              >
              <b-button v-else class="mr-1" disabled size="sm">-</b-button>
              <b-button
                v-if="index === queryFieldsetCount"
                class="mr-1"
                size="sm"
                @click="incrementQueryFieldset"
                >+</b-button
              >
            </b-col>
          </b-input-group>
        </b-row>
        <!-- THIS IS NOT SOMETHING I'M SUPER PROUD OF. -->
        <!-- I was in a hurry to format this bit slightly differently whether the user was on a mobile 
            browser or not so it conditionally renders one version or the other depending... -->
        <!-- Mobile version -->
        <b-row v-if="userOnMobile">
          <b-col cols="12" class="mt-1">
            <div style="float: right; display: inline">
              <!-- <span v-if="options.totalRows <= selectedRecordsLimit">
                <b-button
                  class="ml-1"
                  size="sm"
                  @click="clearAllRows"
                  v-if="allRowsSelected"
                >
                  Clear All Rows
                </b-button>
                <b-button class="ml-1" size="sm" @click="selectAllRows" v-else>
                  Select All Rows
                </b-button>
              </span>
              <span v-else>
                <b-button class="ml-1" size="sm" disabled>
                  Select All Rows
                </b-button>
              </span> -->
              <b-button
                class="ml-1"
                style="width: 90px"
                size="sm"
                @click="searchByKeyword"
              >
                Search
              </b-button>
              <b-button
                class="ml-1"
                style="width: 90px"
                size="sm"
                @click="clear"
              >
                Clear
              </b-button>
              <b-button class="ml-1"
                style="width: 90px;" variant="primary" size="sm" @click="downloadCSVModal = true"
              >
                Download
              </b-button>
            </div>
          </b-col>
        </b-row>
        <b-row class="mt-4 mb-1" v-if="userOnMobile">
          <b-col cols="12">
            <div
              v-if="options.totalRows > -1"
              style="position: absolute; right: 0; bottom: -5px"
            >
              {{ options.totalRows }} records returned
            </div>
          </b-col>
        </b-row>
        <!-- Desktop version -->
        <b-row class="mb-2 mt-3" v-else>
          <b-col cols="5">
            <p class="mb-0">
              Click any row in the table to view its details page or click the
              checkbox beside any number of rows and then click the download
              button to download their data.
            </p>
          </b-col>
          <b-col cols="4" class="mt-1">
            <div style="float: right; display: inline">
              <!-- <span v-if="options.totalRows <= selectedRecordsLimit">
                <b-button
                  class="ml-1"
                  size="sm"
                  @click="clearAllRows"
                  v-if="allRowsSelected"
                >
                  Clear All Rows
                </b-button>
                <b-button class="ml-1" size="sm" @click="selectAllRows" v-else>
                  Select All Rows
                </b-button>
              </span>
              <span v-else>
                <b-button
                  class="ml-1"
                  size="sm"
                  disabled
                  title="Query must return 3000 rows or less."
                >
                  Select All Rows
                </b-button>
              </span> -->
              <b-button
                class="ml-1"
                style="width: 90px"
                size="sm"
                @click="searchByKeyword"
              >
                Search
              </b-button>
              <b-button
                class="ml-1"
                style="width: 90px"
                size="sm"
                @click="clear"
              >
                Clear
              </b-button>
              <b-button class="ml-4"
                style="width: 90px;" variant="primary" size="sm" @click="downloadCSVModal = true"
              >
                Download
              </b-button>
            </div>
          </b-col>
          <b-col cols="3">
            <div
              style="position: relative; height: 100%; width: 100%"
              v-if="!userOnMobile"
            >
              <!-- <span v-if="admin_authorized">
                <b-button class="mt-1 ml-0" style="width: 120px;" variant="danger" size="sm" @click="downloadAllAsCSV">
                  Download ALL
                </b-button>
              </span> -->
              <div
                v-if="options.totalRows > -1"
                style="position: absolute; right: 0; bottom: -5px"
              >
                {{ options.totalRows }} records returned
              </div>
            </div>
          </b-col>
        </b-row>
      </div>
      <div id="content-div" class="flex-row content" style="">
        <b-table
          ref="table"
          striped
          hover
          responsive
          selectable
          select-mode="single"
          bordered
          head-variant="light"
          :sticky-header="btableMaxHeight"
          no-border-collapse
          style="overflow-x: auto"
          @row-selected="onRowSelected"
          :items="myProvider"
          :fields="fields"
          :busy.sync="loading"
          :current-page="options.currentPage"
          :per-page="options.perPage"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          tbody-tr-class="vt-table-rows"
        >
          <!-- A custom formatted header cell to allow for the displaying of header tooltips -->
          <template v-slot:head()="field">
            <span
              :title="field.field.title"
              style="font-size: small; white-space: nowrap"
            >
              {{ field.label }}
            </span>
          </template>
          <!-- add tooltips for each cell -->
          <!-- RIGHT NOW this is only working on columns with simple output. If the value contains some kind 
                 of composit value like the SpeciesName column on the VecDyn Explorer, the tooltip doesn't show up.-->
          <template v-slot:cell()="data">
            <span :title="data.value">{{ data.value }}</span>
          </template>
          <!-- A custom formatted header cell to center the '✓' column header -->
          <!-- <template v-slot:head(✓)="field"> -->
            <!-- Add tooltip and CSS styling.
            <span
              :title="'Select one or more rows and then click Download to download the selected datasets.'"
              style="font-size: small; white-space: nowrap"
            >
              <center>
                {{ field.label }}
              </center>
            </span>
          </template> -->
          <!--  -->

          <!-- A custom formatted header cell to center the '✓' column header -->
          <template v-slot:head(✓)>
            <!-- Add tooltip and CSS styling. -->
            <span
              :title="'Select one or more rows and then click Download to download the selected datasets.'"
              style="font-size: small; white-space: nowrap"
            >
              <!-- {{ field.label }} -->
              <!-- <b-form-select
                v-model="selectionMode"
                :options="checkDropdownOptions"
              > -->
              <b-form-select
                v-model="selectionMode"
              >
                <option
                  :value="''"
                  title="Select an item."
                  disabled
                >Select</option>
                <option
                  :value="'all'"
                  title="Select ALL rows. This item is only available if the query returns 3000 rows or less."
                  disabled
                  v-if="options.totalRows > selectedRecordsLimit"
                >All</option>
                <option
                  :value="'all'"
                  title="Select ALL rows. This item is only available if the query returns 3000 rows or less."
                  v-else
                >All</option>
                <option
                  :value="'page'"
                  title="Select all rows on the current page."
                >All on page</option>
                <option
                  :value="'none'"
                  title="Clear all selected rows."
                >None</option>
              </b-form-select>
            </span>
          </template>
          <!-- Special rendering for the "Variables" column. The content of this field comes from the back end 
                as an array. By default, it would just be displayed as an array. This allows us to display each 
                element with a comma between. -->
          <template v-slot:cell(Variables)="data">
            <span
              :title="data.value[0] + ', ' + data.value[1]"
              v-if="data.value[1]"
            >
              {{ data.value[0] }}, {{ data.value[1] }}
            </span>
            <span :title="data.value[0]" v-else>
              {{ data.value[0] }}
            </span>
          </template>
          <!-- Special rendering for the "Tags" column. The content of this field comes from the back end 
                as an array. Display each tag on a separate line. -->
          <template v-slot:cell(Tags)="data">
            <span v-for="index in data.value" :key="index">
              {{ index }} <br>
            </span>
          </template>
          <!-- Special rendering for the "DOI" column: remove protocol from URL -->
          <template v-slot:cell(DOI)="data">
            <span :title="data.value">
              {{ doiString(data.value) }}
            </span>
          </template>
          <!-- Replace rows with a busy spinner while the table data is loading. -->
          <!-- https://bootstrap-vue.org/docs/components/table#table-busy-state -->
          <template #table-busy>
            <div class="text-danger my-2">
              <b-spinner class="align-left"></b-spinner>
              <strong>&nbsp;Loading...</strong>
            </div>
          </template>
          <!-- add check boxes -->
          <template v-slot:cell(✓)="data">
            <b-form-checkbox
              v-model="selectedRows"
              :value="data.item.DatasetID"
              checked="true"
            ></b-form-checkbox>
          </template>
        </b-table>
      </div>
      <div class="flex-row height-by-content">
        <!-- Adds and configures the pagination object at the bottom right of the table. -->
        <b-pagination
          v-if="options.totalRows"
          v-model="options.currentPage"
          :total-rows="options.totalRows"
          :per-page="options.perPage"
          align="right"
          size="sm"
          class="my-0 mb-2"
        ></b-pagination>
      </div>
    </div>
  </VTBaseTemplate>
</template>

<script>
import * as _ from "lodash";
// import { mapState } from "vuex";
import { mapActions, mapState } from "vuex";
import VTBaseTemplate from "@/layouts/VTBaseTemplate.vue";
import HandleErrors from "@/utilities/handle-errors";
import ExplorerInstructions from "@/components/instructions/ExplorerInstructions.vue";
import { HTTP } from "@/utilities/http-common";
import {
  getOperatorOptions,
  getArrayDiffIndex,
  extractArrayFromQuery,
} from "@/utilities/search-form-helpers";
import BaseDialogMixin from "@/components/mixins/BaseDialogMixin.vue";
import BaseAlertMixin from "@/components/mixins/BaseAlertMixin.vue";
export default {
  name: "vectraits-explorer",
  mixins: [VTBaseTemplate, BaseDialogMixin, BaseAlertMixin],
  components: {
    VTBaseTemplate,
    ExplorerInstructions,
  },
  data() {
    return {
      data: [],
      fields: [], // Dynamically populated by buildFieldsArray(). Fed to the b-table to determine what columns show.
      fieldDefs: [], // Populated by getFieldDefs(). Stores the definition objects for each VecTraits Column.
      options: {
        // used by the table pagination object and the display of total rows at top of table
        currentPage: 1,
        totalRows: 0,
        perPage: 50,
      },
      loading: false, // controls whether the table spinner displays or not
      selectionMode: '',
      selectedRows: [], // holds the rows that have been 'checked' by the user.
      queryFieldsetCount: 1, // the number of sets of query fields
      fieldArray: [], // array of column arguments from the 'Select a column' fields
      operatorArray: [], // array of operator arguments from the 'Select an operator' fields
      termArray: [], // array of search arguments from the 'Enter a search term...' fields
      keywords: "", // space delimited list of search terms from the 'Search by phrase...' field
      sortDesc: false, // sort decending?
      fieldDropdownOptions: [], // this is now being built dynamically with buildOptions()
      operatorOptionsArray: [],
      sortBy: "DatasetID", // by default, sort by 'ID'
      title: "VecTraits Explorer", // when the ExplorerSearchFormAndTable component is implemented on this page, you'll need to pass this as a prop to it.
      totalItems: 0, // initialize totalItems to 0 until query results come back
      err_msg: "", // If any errors during a call to the backend, that error content will end up in this variable and be displayed on the modal.
      route_err: "", // If there were errors on the $route, they end up in this variable. There were a few issues before when there would be an error output to the console but it would work just fine. This is a hackey way to do things but it worked.
      apiErrModalShow: false, // show error modal or not
      downloadCSVModal: false,
      allRowIDs: [], // array that holds all IDs of current query results. This is passed from the back end.
      allRowsSelected: false, // it's much easier to download all records by passing a boolean flag than pass all IDs.
      selectedRecordsLimit: 3000, // Currently, the backend will only allow 3000 rows to be selected. More than that and it will return an error.
      btableMaxHeight: "600px", // This is the default height but it gets changed by resizeTableDiv on page load and window resize events.
      logoWidth: "45px", // Default logo size. Will be changed if the user is on a mobile browser.
    };
  },
  watch: {
    view: function () {
      this.$refs.table.refresh();
    },
    "options.currentPage"() {
      this.prependHTTPToDOI();
      this.addUrlQueryParams();
    },
    sortBy: function () {
      this.prependHTTPToDOI();
      this.addUrlQueryParams();
    },
    sortDesc: function () {
      this.prependHTTPToDOI();
      this.addUrlQueryParams();
    },
    allRowIDs: function () {
      this.checkSelectedRows();
    },
    compFieldArray: {
      // This is needed to change the options in the Operator drop down beside each field selector.
      handler(newValue, oldValue) {
        let fieldIndex = getArrayDiffIndex(newValue, oldValue);
        this.operatorOptionsArray[fieldIndex] = getOperatorOptions(
          this.fieldArray[fieldIndex]
        );
      },
      deep: true,
    },
    selectionMode : {
      handler() {
        if (this.selectionMode == 'all') {
          this.selectAllRows();
        } else if (this.selectionMode == 'page') {
          this.selectPageRows();
        // } else if (this.selectionMode == 'none') {
        //   this.clearAllRows();
        } else {
          this.clearAllRows();
        }
      }
    },
    selectedRows: {
      // Store the selected rows in the store so that we can get them back if the user navigates away and comes back.
      handler() {
        this.setVTXCheckBoxes(this.selectedRows);
        // console.log(this.vtx_checkboxes);
      }
    }
  },
  beforeRouteUpdate(to, from, next) {
    // This router-guard is meant to refresh the table whenever the URL query params are updated. This is mostly
    // to handle the back/forward buttons in the browser. When the user clicks back, it will update the URL
    // params. We want the table to update according to what the URL displays.
    this.unpackUrlQueryParams(to.query);
    this.$refs.table.refresh();
    next();
  },
  computed: {
    compFieldArray() {
      // Wrapping fieldArray in a computed property because, when watching an array, you can't see the old value
      // of the array. The oldVal will show the same values as the newVal version of the array. Making this a
      // computed property will allow the watcher to see both values.
      // https://forum.vuejs.org/t/get-the-array-item-that-had-a-changed-value/31110/3
      return this.fieldArray.slice(0);
    },
    ...mapState("user", ["user", "myGroups"]),
    ...mapState('vtxcheckboxes', ['vtx_checkboxes']),
    admin_authorized : function() {
      const that = this;
      // store.getters['user/inMyGroups'](['admin', 'uploader', 'collaborator'])
      let groups = ['admin', 'uploader', 'collaborator'];
      return Boolean((_.intersection(that.myGroups, groups)).length);
    },
  },
  methods: {
    ...mapActions("vtxcheckboxes", [
      "clearVTXCheckBoxes",
      "setVTXCheckBoxes"
    ]),
    hideModal() {
      this.apiErrModalShow = false;
      this.downloadCSVModal = false;
    },
    hasHTTP(termString) {
      if (
        termString.startsWith("http://") ||
        termString.startsWith("https://")
      ) {
        return true;
      } else {
        return false;
      }
    },
    hasPipe(termString) {
      if (termString.includes("|")) {
        return true;
      } else {
        return false;
      }
    },
    /**
     * prependHTTPToDOI
     * For each term, if it is a DOI, prepend "http://" to it if it does not already have it at the beginning.
     */
    prependHTTPToDOI() {
      const that = this;
      let k;
      for (k = 0; k < that.termArray.length; k++) {
        if (that.termArray[k].length != 0) {
          if (that.fieldArray[k] === "DOI" && that.operatorArray[k] !== 'contains' && that.operatorArray[k] !== 'ncontains') {
            // if the term has pipe-separated terms, split them and prepend the http:// to each.
            if (that.hasPipe(that.termArray[k])) {
              const pipeTerms = that.termArray[k].split("|");
              const fixedTerms = pipeTerms.map((term) => {
                if (!that.hasHTTP(term)) {
                  return "http://" + term;
                } else {
                  return term;
                }
              });
              // rejoin each term back to a single, pipe-separated string.
              that.termArray[k] = fixedTerms.join("|");
            } else {
              if (!that.hasHTTP(that.termArray[k])) {
                that.termArray[k] = `http://${that.termArray[k]}`;
              }
            }
          }
        }
      }
    },
    myProvider(ctx) {
      // This function gets called by the table itself and everytime you see "this.$refs.table.refresh();"
      // Packs up all of the search criteria and sends it to the back end then returns whatever it sends back
      // or an error.
      this.$bvModal.hide("modal-1");
      const that = this;
      // build the params object to be sent with the request.
      var params = new URLSearchParams();
      params.append("page", ctx.currentPage);
      params.append("keywords", that.keywords);
      params.append("sort_column", that.sortBy);
      params.append("sort_dir", that.sortDesc ? "desc" : "asc");
      // load fieldArray, operatorArray, and termArray into the params obj
      var i;
      for (i = 0; i < that.fieldArray.length; i++) {
        if (that.fieldArray[i].length != 0) {
          params.append("field", that.fieldArray[i]);
        }
      }
      var j;
      for (j = 0; j < that.operatorArray.length; j++) {
        if (that.operatorArray[j].length != 0) {
          params.append("operator", that.operatorArray[j]);
        }
      }
      var k;
      for (k = 0; k < that.termArray.length; k++) {
        if (that.termArray[k].length != 0) {
          params.append("term", that.termArray[k]);
        }
      }
      var request = {
        params: params,
      };
      return HTTP.get(`/api/vectraits-explorer/`, request)
        .then((response) => {
          that.data = response.data.data.results;
          that.options.totalRows = parseInt(response.data.data.count);
          that.allRowIDs = response.data.ids;
          this.csvEndpoint = response.request.responseURL;

          return that.data;
        })
        .catch((error) => {
          this.err_msg = HandleErrors.HandleErrors(error);
          this.apiErrModalShow = true;
          return [];
        });
    },
    getFieldDefs() {
      const that = this;
      return HTTP.get(`/api/column-definitions/`)
        .then((response) => {
          that.fieldDefs = response.data;
        })
        .catch((error) => {
          this.err_msg = HandleErrors.HandleErrors(error);
          this.apiErrModalShow = true;
        });
    },
    onRowSelected(items) {
      this.$router.push({
        name: "vectraits-dataset",
        params: { id: items[0]["DatasetID"] },
      });
    },
    selectAllRows() {
      this.allRowsSelected = true;
      this.selectedRows = this.allRowIDs;
    },
    selectPageRows() {
      // this.allRowsSelected = false;
      this.selectedRows = [];
      // const collection = document.getElementsByName("chk");  // Because we're using bootstrap-vue, this won't work
      const collection = document.getElementsByClassName("custom-control-input");  // get all <b-form-checkbox> objs
      for (let i = 0; i < collection.length; i++) {  // Loop through all the b-f-checkboxes
        if (collection[i].type == "checkbox") {  // Make sure we're only dealing with the checkbox itself
          collection[i].checked = true;  // set to true to "check" it.
          this.selectedRows.push(collection[i].value);  // Add the row's value to the selectedRows array.
        }
      }
    },
    clearAllRows() {
      // clear any selected checkboxes
      this.selectedRows = [];
      // this.clearVTXCheckBoxes();
      this.allRowsSelected = false;
    },
    beforeRouteLeave(to, from, next) {
      this.$bvModal.hide("modal-1");
      next();
    },
    incrementQueryFieldset() {
      // gets called when the user clicks the '+' button on the right of the search form
      this.queryFieldsetCount += 1;
    },
    removeQueryFieldset(index) {
      // gets called when the user clicks the '-' button on the right of the search form
      this.queryFieldsetCount -= 1;
      this.fieldArray.splice(index, 1);
      this.operatorArray.splice(index, 1);
      this.termArray.splice(index, 1);
    },
    searchByKeyword() {
      // When the Search button is clicked, reset defaults and get results
      // We always want to start on the first page of our results
      this.options.currentPage = 1;
      this.selectedRows = [];
      this.prependHTTPToDOI();
      this.addUrlQueryParams();
    },
    buildFieldsArray(defs) {
      // Gets called by the created() lifecycle hook. Build the fields array which is used by the main b-table
      // NOTE: IF THERE ARE NO ROWS IN DATA, THIS WILL ERROR OUT.
      // TODO: handle no rows in input. Currently, we are checking that there are rows before calling this
      // function in myProvider().
      let def_dict = {};
      defs.forEach(function (obj) {
        def_dict[obj.field_name] = obj.definition;
      });
      let fields = [
        { key: "✓", sortable: false, tdClass: "vt-table-cell-mid center-checkbox" },
        {
          key: "DatasetID",
          sortable: true,
          tdClass: "text-truncate vt-table-cell text-underline",
          title: def_dict["DatasetID".toLowerCase()],
        },
        {
          key: "OriginalTraitName",
          sortable: true,
          tdClass: "text-truncate vt-table-cell-wide",
          title: def_dict["OriginalTraitName".toLowerCase()],
        },
        {
          key: "Variables",
          sortable: true,
          tdClass: "text-truncate vt-table-cell-wide",
          title: def_dict["Variables".toLowerCase()],
        },
        {
          key: "Tags",
          sortable: true,
          tdClass: "text-truncate vt-table-cell-wide",
          title: def_dict["Tags".toLowerCase()],
        },
        {
          key: "Interactor1Order",
          sortable: true,
          tdClass: "text-truncate vt-table-cell",
          title: def_dict["Interactor1Order".toLowerCase()],
        },
        {
          key: "Interactor1Family",
          sortable: true,
          tdClass: "text-truncate vt-table-cell",
          title: def_dict["Interactor1Family".toLowerCase()],
        },
        {
          key: "Interactor1Stage",
          sortable: true,
          tdClass: "text-truncate vt-table-cell",
          title: def_dict["Interactor1Stage".toLowerCase()],
        },
        {
          key: "Interactor1Genus",
          sortable: true,
          tdClass: "text-truncate vt-table-cell",
          title: def_dict["Interactor1Genus".toLowerCase()],
        },
        {
          key: "Interactor1Species",
          sortable: true,
          tdClass: "text-truncate vt-table-cell",
          title: def_dict["Interactor1Species".toLowerCase()],
        },
        {
          key: "Interactor2Genus",
          sortable: true,
          tdClass: "text-truncate vt-table-cell",
          title: def_dict["Interactor2Genus".toLowerCase()],
        },
        {
          key: "Interactor2Species",
          sortable: true,
          tdClass: "text-truncate vt-table-cell",
          title: def_dict["Interactor2Species".toLowerCase()],
        },
        {
          key: "Citation",
          sortable: true,
          tdClass: "text-truncate vt-table-cell",
          title: def_dict["Citation".toLowerCase()],
        },
        {
          key: "DOI",
          sortable: true,
          tdClass: "text-truncate vt-table-cell",
          title: def_dict["DOI".toLowerCase()],
        },
        {
          key: "CuratedByDOI",
          sortable: true,
          tdClass: "text-truncate vt-table-cell",
          title: def_dict["CuratedByDOI".toLowerCase()],
        },
        {
          key: "SubmittedBy",
          sortable: true,
          tdClass: "text-truncate vt-table-cell",
          title: def_dict["SubmittedBy".toLowerCase()],
        },
      ];
      if (this.admin_authorized == true) {
          fields.push({key:"EmbargoReleaseDate", sortable:true, tdClass:"vt-table-cell",},);
      }
      return fields
    },
    buildFieldDropdownOptions() {
      // Populate the columns drop-down using the keys from the fields array
      this.fieldDropdownOptions = [];
      // Add a default value to display a placeholder help text.
      this.fieldDropdownOptions.push({
        value: "",
        text: "Select a column",
        disabled: true,
      });
      this.fields.forEach((field) => {
        this.fieldDropdownOptions.push({ value: field.key, text: field.key });
      });
      // remove the "Select" field from the fieldDropdownOptions array
      this.fieldDropdownOptions.splice(1, 1);
    },
    downloadCSV() {
      // Called by the "Download" button. Calls the back end to download the rows belonging to datasets with a
      // checked checkbox.
      this.downloadCSVModal = false;
      if (!this.selectedRows.length) {
        alert("You have not selected any rows!!!!");
        return;
      }
      const fd = new FormData();
      this.selectedRows.forEach((row_id) => {
        fd.append("ids", row_id);
      });
      fd.append("return_as", "csv");
      HTTP.post(`/api/vectraits-get-populated-fields/`, fd)
        .then((response) => {
          var fileURL = window.URL.createObjectURL(new Blob([response.data]));
          var fileLink = document.createElement("a");
          fileLink.href = fileURL;
          const now = new Date();
          var fileName = "vec_traits_download_" + now + ".csv";
          fileLink.setAttribute("download", fileName);
          document.body.appendChild(fileLink);
          fileLink.click();
          this.$notify({
            text: "Download successful!",
            duration: 10000,
            type: "success",
          });
        })
        .catch((error) => {
          this.err_msg = HandleErrors.HandleErrors(error);
          this.apiErrModalShow = true;
        });
    },
    downloadAllAsCSV() {
      // Called by the "Download ALL" button. Calls the back end to download ALL VT data

      HTTP.get(`/api/vectraits-download-all/`)
        .then((response) => {
          var fileURL = window.URL.createObjectURL(new Blob([response.data]));
          var fileLink = document.createElement("a");
          fileLink.href = fileURL;
          const now = new Date();
          var fileName = "vec_traits_all_download_" + now + ".csv";
          fileLink.setAttribute("download", fileName);
          document.body.appendChild(fileLink);
          fileLink.click();
          this.$notify({
            text: "Download successful!",
            duration: 10000,
            type: "success",
          });
        })
        .catch((error) => {
          this.err_msg = HandleErrors.HandleErrors(error);
          this.apiErrModalShow = true;
        });
    },
    clear() {
      // called by the clear button. Resets the page to the initial state.
      this.options.currentPage = 1;
      this.queryFieldsetCount = 1;
      this.fieldArray = [];
      this.operatorArray = [];
      this.termArray = [];
      this.keywords = "";
      this.sortBy = "DatasetID";
      this.sortDesc = false;
      this.selectedRows = [];
      this.$refs.table.refresh();
      // set the default values for the select column and select operator fields to display default text
      this.fieldArray[0] = "";
      this.operatorArray[0] = "";
      this.clearUrlQueryParams();
    },
    unpackUrlQueryParams(query_obj) {
      // if there are query params in the URL fetch them and load them into local vars. For example:
      // http://localhost:8080/vectraits-datasets?keywords=aedes&field=DatasetID&field=OriginalTraitName&operator=sw&operator=contains&term=MSS&term=wing
      this.keywords = "";
      if ("keywords" in query_obj) {
        this.keywords = query_obj.keywords;
      }
      // this.keywords += ' ';
      this.fieldArray = undefined;
      this.operatorArray = undefined;
      this.termArray = undefined;
      this.fieldArray = extractArrayFromQuery(query_obj, "field");
      this.operatorArray = extractArrayFromQuery(query_obj, "operator");
      this.termArray = extractArrayFromQuery(query_obj, "term");
      this.queryFieldsetCount = this.fieldArray.length;
      this.sortBy = "DatasetID";
      if ("sort_by" in query_obj) {
        this.sortBy = query_obj.sort_by;
      }
      this.sortDesc = false;
      if ("sort_desc" in query_obj) {
        this.sortDesc = true;
      }
      this.options.currentPage = 1;
      if ("page" in query_obj) {
        this.options["currentPage"] = query_obj.page;
      }
    },
    addUrlQueryParams() {
      // when called, add the values of local vars into the URL as query params.
      let query = {};
      if (this.keywords) {
        query["keywords"] = this.keywords;
      }
      if (this.fieldArray[0]) {
        query["field"] = this.fieldArray;
      }
      if (this.operatorArray[0]) {
        query["operator"] = this.operatorArray;
      }
      if (this.termArray[0]) {
        query["term"] = this.termArray;
      }
      if (this.sortBy != "DatasetID" && this.sortBy != "") {
        query["sort_by"] = this.sortBy;
      }
      if (this.sortDesc) {
        query["sort_desc"] = true;
      }
      // There were a lot of issues with the route erroring out because it thought we were navigating to and
      // from the same route (and maybe we are but it's not super clear?). It would then just cancel the
      // navigation. A hackey workaround was to add a '__key' param which just gets the value of the current
      // moment. That ensures that the route always *looks* different to the Vue Router. I'm sure there's a
      // better way to do that but here it is for now.
      // https://github.com/vuejs/vue-router/issues/3013
      // https://github.com/vuejs/vue-router/issues/2872
      query["__key"] = Date.now();
      // console.log("    Query obj: ", query);
      if (this.options.currentPage != 1) {
        query["page"] = this.options.currentPage;
      }
      this.$router.push({ query }).catch((err) => {
        this.route_err = err;
      });
    },
    clearUrlQueryParams() {
      // clear URL query params
      this.$router.push({ query: {} }).catch((err) => {
        this.route_err = err;
      });
    },
    resizeTableDiv() {
      // gets called when the page loads for the first time and every time the browser window is resized.
      // Sets the div containing the b-table to take up all space between the search form and the pagination
      // controls so that there is no scroll bar on the right. (There will likely be a scrollbar on the table
      // itself. 2 scrollbars is 2 many...)
      let divElement = document.querySelector("#content-div");
      let elemHeight = divElement.clientHeight;
      this.btableMaxHeight = (elemHeight - 70).toString() + "px";
    },
    checkSelectedRows() {
      if (this.options.totalRows > this.selectedRecordsLimit) {
        this.allRowsSelected = false;
      } else {
        if (this.allRowsSelected) {
          this.selectedRows = this.allRowIDs;
        }
      }
    },
    doiString: function (doi) {
      // Remove the protocol prefix from the DOI and return the remaining substring.
      if (doi == null) {
        return null;
      }
      if (doi.startsWith("http://")) {
        return doi.replace("http://", "");
      } else if (doi.startsWith("https://")) {
        return doi.replace("https://", "");
      }
      return doi;
    },
    getLogoWidth() {
      // Shrink the logo if user is on a mobile browser.
      if (this.userOnMobile) {
        this.logoWidth = "25px";
      }
    },
  },
  // LIFECYCLE HOOKS -----------------------------
  created() {
    // set the default values for the select column and select operator fields to display default text
    document.title = "VecTraits Explorer";
    this.fieldArray[0] = "";
    this.operatorArray[0] = "";
    this.unpackUrlQueryParams(this.$route.query);
    window.addEventListener("resize", this.resizeTableDiv);
    this.fieldDefs = this.getFieldDefs().then(() => {
      this.fields = this.buildFieldsArray(this.fieldDefs);
      this.buildFieldDropdownOptions();
    });    
  },
  mounted() {
    this.resizeTableDiv();
    this.checkSelectedRows();
    this.getLogoWidth();

    // get any checkboxes from the Store if there are any:
    this.selectedRows = this.vtx_checkboxes;
  },
  destroyed() {
    window.removeEventListener("resize", this.resizeTableDiv);
  },
};
</script>

<style scoped>
h1,
h2,
a,
p {
  color: black !important;
  text-shadow: none;
}
p {
  font-size: smaller;
}
.table td,
.table th {
  padding: 6px !important;
}
.title-area h2,
h5 {
  display: inline;
  vertical-align: middle;
}
</style>
